import { useEffect, useRef, useState } from 'react';

// Shared observer for performance - one observer for all elements
const observers = new Map();

const getObserver = (threshold, rootMargin) => {
  const key = `${threshold}-${rootMargin}`;
  if (observers.has(key)) return observers.get(key);

  const callbacks = new Map();
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach(entry => {
        const cb = callbacks.get(entry.target);
        if (cb) cb(entry.isIntersecting);
      });
    },
    { threshold, rootMargin }
  );

  const shared = { observer, callbacks };
  observers.set(key, shared);
  return shared;
};

export const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const threshold = options.threshold || 0.1;
  const rootMargin = options.rootMargin || '0px 0px -50px 0px';

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef) return;

    const { observer, callbacks } = getObserver(threshold, rootMargin);
    callbacks.set(currentRef, setIsVisible);
    observer.observe(currentRef);

    return () => {
      callbacks.delete(currentRef);
      observer.unobserve(currentRef);
    };
  }, [threshold, rootMargin]);

  return [ref, isVisible];
};

// Lightweight animation wrapper - uses only opacity + transform for 60fps
export const AnimateOnScroll = ({ 
  children, 
  className = '', 
  animation = 'fade-up',
  delay = 0,
  duration = 0.4
}) => {
  const [ref, isVisible] = useScrollAnimation();

  const transforms = {
    'fade-up': 'translateY(20px)',
    'fade-down': 'translateY(-20px)',
    'fade-left': 'translateX(-20px)',
    'fade-right': 'translateX(20px)',
    'scale': 'scale(0.95)',
    'fade': 'none'
  };

  const t = transforms[animation] || transforms['fade-up'];

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'none' : t,
        transition: `opacity ${duration}s ease ${delay}s, transform ${duration}s ease ${delay}s`,
        willChange: isVisible ? 'auto' : 'opacity, transform'
      }}
    >
      {children}
    </div>
  );
};

export default useScrollAnimation;
